import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Button } from "@nosible/shared/components/ui/button";
import { Table as TableIcon, List } from "lucide-react";
import { intervalToDuration } from "date-fns";
import type { RateLimitEntry } from "./RateLimits";

type RateLimitsByType = {
  [type: string]: {
    [interval: number]: number;
  };
};

type Props = {
  queryLimits: RateLimitEntry[];
};

const formatIntervalLabel = (seconds: number): string => {
  if (seconds < 60) {
    return `per ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  }

  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

  const parts: string[] = [];
  if (duration.years) parts.push(`${duration.years} year${duration.years !== 1 ? "s" : ""}`);
  if (duration.months) parts.push(`${duration.months} month${duration.months !== 1 ? "s" : ""}`);
  if (duration.weeks) parts.push(`${duration.weeks} week${duration.weeks !== 1 ? "s" : ""}`);
  if (duration.days) parts.push(`${duration.days} day${duration.days !== 1 ? "s" : ""}`);
  if (duration.hours) parts.push(`${duration.hours} hour${duration.hours !== 1 ? "s" : ""}`);
  if (duration.minutes) parts.push(`${duration.minutes} minute${duration.minutes !== 1 ? "s" : ""}`);

  if (parts.length === 0) {
    return `per ${seconds} seconds`;
  }

  return `per ${parts.join(", ")}`;
};

const groupLimitsByType = (limits: RateLimitEntry[]): RateLimitsByType => {
  const grouped: RateLimitsByType = {};

  for (const limit of limits) {
    if (!grouped[limit.type]) {
      grouped[limit.type] = {};
    }
    grouped[limit.type]![limit.interval] = limit.limit;
  }

  return grouped;
};

const getUniqueIntervals = (limits: RateLimitEntry[]): number[] => {
  const intervals = new Set(limits.map(l => l.interval));
  return Array.from(intervals).sort((a, b) => a - b);
};

export function RateLimitDisplay({ queryLimits }: Props) {
  const [viewMode, setViewMode] = useState<"table" | "list">("table");

  useEffect(() => {
    if (window.matchMedia("(max-width: 520px)").matches) {
      setViewMode("list");
    }
  }, []);

  const grouped = groupLimitsByType(queryLimits);
  const intervals = getUniqueIntervals(queryLimits);
  const types = Object.keys(grouped).sort();

  const getLimitValue = (type: string, interval: number): number | undefined => {
    return grouped[type]?.[interval];
  };

  if (types.length === 0) {
    return (
      <div className="text-center py-4 text-chromium-mist">
        No rate limits defined
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center border border-slate-core rounded-md">
          <Button
            variant={viewMode === "table" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("table")}
            className="rounded-r-none"
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
            className="rounded-l-none"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {viewMode === "list" ? (
        <div className="rate-limit-list space-y-3">
          {Object.entries(grouped).flatMap(([type, typeIntervals]) =>
            Object.entries(typeIntervals).map(([intervalStr, limit]) => {
              const interval = parseInt(intervalStr, 10);
              return (
                <div
                  key={`${type}-${interval}`}
                  className="rate-limit-list__row flex items-center justify-between p-3 bg-slate-core/30 rounded-lg border border-slate-core"
                >
                  <div className="rate-limit-list__identity flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-silica capitalize">{type}</span>
                      <span className="text-chromium-mist">•</span>
                      <span className="text-silica">{formatIntervalLabel(interval)}</span>
                      <span className="text-chromium-mist text-sm">({interval}s)</span>
                    </div>
                  </div>
                  <span className="rate-limit-list__value text-silica font-medium">
                    {limit.toLocaleString()} requests
                  </span>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <Table className="admin-wide-table">
          <TableHeader>
            <TableRow className="bg-slate-core/50">
              <TableHead className="w-32 font-bold text-silica">Type</TableHead>
              {intervals.map((interval) => (
                <TableHead key={interval} className="font-bold text-silica">
                  <div className="flex flex-col items-start">
                    <span>{formatIntervalLabel(interval)}</span>
                    <span className="text-chromium-mist text-sm font-normal">({interval}s)</span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type}>
                <TableCell className="font-bold text-silica capitalize">
                  {type}
                </TableCell>
                {intervals.map((interval) => {
                  const limitValue = getLimitValue(type, interval);
                  return (
                    <TableCell key={interval}>
                      <span className="text-chromium-mist">
                        {limitValue !== undefined ? `${limitValue.toLocaleString()} requests` : "NA"}
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
